import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Paper,
  Typography,
  Button,
  Chip,
  Avatar,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  TABLE_PAPER_SX,
  HEAD_CELL_SX,
  BODY_CELL_SX,
  TABLE_ROW_SX,
  AVATAR_SX,
} from '../common/tableStyles';
import {
  Download as DownloadIcon,
  CalendarToday as CalendarIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  PeopleAlt as PeopleIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import SheduleBox from './SheduleBox';

// ---- FORTHEYE soft-card tokens (shared with the rest of the portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';
const GRADIENT_BTN = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: '12px',
  background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
  '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' },
};
const OUTLINED_BTN = {
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: '12px',
  color: '#6b7280',
  borderColor: '#e5e7eb',
};

const Total = () => {
  const [applicantsData, setApplicantsData] = useState([]);
  const [disabledButtons, setDisabledButtons] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [selectedDate] = useState(null);
  const [loading, setLoading] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const formattedDate = (date) => dayjs(date).format('YYYY-MM-DD');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://www1.adityatrading.in/careers/api/applied-candidates/', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

        const data = await response.json();
        setApplicantsData(data.data || []);
      } catch (error) {
        console.error('Error fetching the data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);



  const handleScheduleClick = (index, applicant) => {
    if (applicant) {
      setSelectedApplicant(applicant);
      setOpenModal(true);
      setDisabledButtons((prev) => ({ ...prev, [index]: true }));
    } else {
      console.error('No date selected for applicant:', applicant);
    }
  };



  // Mobile Card View Component
  const MobileCardView = ({ applicant, index }) => (
    <Card
      elevation={0}
      sx={{
        mb: 2,
        borderRadius: '16px',
        border: '1px solid #eef0f4',
        boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 12px 28px rgba(17,24,39,0.08)',
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Avatar sx={AVATAR_SX}>
              {(applicant.name || 'N').charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, color: '#111827', mb: 0.5 }}>
                {applicant.name || 'N/A'}
              </Typography>
              <Chip
                label={applicant.role || 'N/A'}
                size="small"
                sx={{ backgroundColor: '#eef4ff', color: ACCENT, fontWeight: 700, border: 'none' }}
              />
            </Box>
          </Box>
          <Chip
            label={`#${index + 1}`}
            size="small"
            sx={{ backgroundColor: '#f3f4f6', color: '#6b7280', fontWeight: 700, border: 'none' }}
          />
        </Box>

        <Stack spacing={1.25} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
            <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>{applicant.email || 'N/A'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhoneIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
            <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>{applicant.phoneNumber || 'N/A'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon sx={{ fontSize: 16, color: '#9ca3af' }} />
            <Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>
              Applied: {formattedDate(applicant.created) || 'N/A'}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {applicant.resumeFile && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
              href={applicant.resumeFile}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ ...OUTLINED_BTN, flex: 1, minWidth: '120px' }}
            >
              Resume
            </Button>
          )}
          {!disabledButtons[index] ? (
            <Button
              variant="contained"
              disableElevation
              size="small"
              onClick={() => handleScheduleClick(index, applicant)}
              sx={{ ...GRADIENT_BTN, flex: 1, minWidth: '120px' }}
            >
              Schedule
            </Button>
          ) : (
            <Button
              variant="contained"
              disableElevation
              size="small"
              disabled
              sx={{ textTransform: 'none', borderRadius: '12px', fontWeight: 700, flex: 1, minWidth: '120px' }}
            >
              Scheduled
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header card */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <PeopleIcon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.35rem', md: '1.7rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                Applied Candidates
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Review applicants and schedule their interviews.
              </Typography>
            </Box>
          </Box>
          <Chip
            label={`${applicantsData.length} Candidate${applicantsData.length === 1 ? '' : 's'}`}
            sx={{ backgroundColor: '#eef4ff', color: ACCENT, fontWeight: 700, borderRadius: '10px', border: 'none' }}
          />
        </Stack>
      </Paper>

      {loading ? (
        <Paper elevation={0} sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5, py: 6 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PeopleIcon />
          </Box>
          <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>Loading candidates...</Typography>
        </Paper>
      ) : (
        <>
          {isMobile ? (
            <Box>
              {applicantsData.length > 0 ? (
                applicantsData.map((applicant, index) => (
                  <MobileCardView key={index} applicant={applicant} index={index} />
                ))
              ) : (
                <Paper elevation={0} sx={{ ...CARD_SX, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5, py: 6 }}>
                  <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PeopleIcon />
                  </Box>
                  <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>No candidates available</Typography>
                </Paper>
              )}
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ ...TABLE_PAPER_SX, borderRadius: '22px', border: '1px solid #eef0f4', boxShadow: '0 6px 24px rgba(17,24,39,0.05)' }}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    {['S.No', 'Name', 'Position', 'Email', 'Contact', 'Resume', 'Applied', 'Action'].map((header) => (
                      <TableCell key={header} sx={HEAD_CELL_SX}>
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {applicantsData.length > 0 ? (
                    applicantsData.map((applicant, index) => (
                      <TableRow key={index} sx={TABLE_ROW_SX}>
                        <TableCell sx={BODY_CELL_SX}>
                          {index + 1}
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={AVATAR_SX}>
                              {(applicant.name || 'N').charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                              {applicant.name || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          <Chip
                            label={applicant.role || 'N/A'}
                            size="small"
                            sx={{ backgroundColor: '#eef4ff', color: ACCENT, fontWeight: 700, border: 'none' }}
                          />
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          {applicant.email || 'N/A'}
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          {applicant.phoneNumber || 'N/A'}
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          {applicant.resumeFile ? (
                            <Box
                              component="a"
                              href={applicant.resumeFile}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: ACCENT,
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              <DownloadIcon sx={{ fontSize: 16 }} />
                              Download
                            </Box>
                          ) : (
                            <Box component="span" sx={{ color: '#9ca3af', fontSize: '0.875rem' }}>N/A</Box>
                          )}
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          {formattedDate(applicant.created) || 'N/A'}
                        </TableCell>
                        <TableCell sx={BODY_CELL_SX}>
                          {!disabledButtons[index] ? (
                            <Button
                              variant="contained"
                              disableElevation
                              size="small"
                              onClick={() => handleScheduleClick(index, applicant)}
                              sx={{ ...GRADIENT_BTN, px: 2, fontSize: '0.8rem' }}
                            >
                              Schedule
                            </Button>
                          ) : (
                            <Button
                              variant="outlined"
                              size="small"
                              disabled
                              sx={{ ...OUTLINED_BTN, px: 2, fontSize: '0.8rem' }}
                            >
                              Scheduled
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 8, border: 'none' }}>
                        <Stack alignItems="center" spacing={1.5}>
                          <Box sx={{ width: 56, height: 56, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <PeopleIcon />
                          </Box>
                          <Typography sx={{ color: '#6b7280', fontWeight: 600 }}>No candidates available</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      <SheduleBox
        open={openModal}
        close={() => setOpenModal(false)}
        applicantData={selectedApplicant}
        selectedDate={selectedDate}
      />
    </Box>
  );
};

export default Total;
